import { useState, useCallback, useEffect, useRef } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { MapPin, Sparkles, Mountain, Trees, Waves, Cloud, Upload } from 'lucide-react'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCommunityStore } from '../../../../stores/communityStore'
import { useCustomValuesStore } from '../../../../stores/customValuesStore'
import { useEnemyStore } from '../../../../stores/enemyStore'
import { useItemStore } from '../../../../stores/itemStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import type { LocationInput } from '@creanizer/types'

interface LocationFormProps {
  onGenerate: (input: LocationInput) => void
  isGenerating: boolean
  initialData?: LocationInput
  useStreaming?: boolean
  onStreamingChange?: (value: boolean) => void
}

const locationTypes = [
  { value: 'mountain', label: '山岳', icon: Mountain },
  { value: 'forest', label: '森林', icon: Trees },
  { value: 'lake', label: '湖', icon: Waves },
  { value: 'river', label: '河川', icon: Waves },
  { value: 'desert', label: '砂漠', icon: Mountain },
  { value: 'plains', label: '平原', icon: Trees },
  { value: 'coast', label: '海岸', icon: Waves },
  { value: 'island', label: '島', icon: Mountain },
  { value: 'valley', label: '谷', icon: Mountain },
  { value: 'canyon', label: '峡谷', icon: Mountain },
  { value: 'tundra', label: 'ツンドラ', icon: Mountain },
  { value: 'jungle', label: 'ジャングル', icon: Trees },
  { value: 'swamp', label: '沼地', icon: Waves },
  { value: 'glacier', label: '氷河', icon: Mountain },
  { value: 'volcano', label: '火山', icon: Mountain },
  { value: 'floating', label: '浮遊大陸', icon: Cloud },
  { value: 'underground_entrance', label: '地下への入口', icon: Mountain },
  { value: 'custom', label: 'カスタム', icon: Sparkles }
]

const genres = [
  { value: 'fantasy', label: 'ファンタジー' },
  { value: 'sf', label: 'SF' },
  { value: 'modern', label: '現代' },
  { value: 'post_apocalypse', label: 'ポストアポカリプス' },
  { value: 'cyberpunk', label: 'サイバーパンク' },
  { value: 'steampunk', label: 'スチームパンク' },
  { value: 'mythology', label: '神話' },
  { value: 'horror', label: 'ホラー' },
  { value: 'urban_fantasy', label: 'アーバンファンタジー' },
  { value: 'custom', label: 'カスタム' }
]

const purposes = [
  { value: 'tourism', label: '観光' },
  { value: 'adventure', label: '冒険' },
  { value: 'mystery', label: '謎・調査' },
  { value: 'sanctuary', label: '聖域' },
  { value: 'battlefield', label: '戦場' },
  { value: 'sacred', label: '神聖な場所' },
  { value: 'forbidden', label: '禁断の地' },
  { value: 'training', label: '修行・訓練' },
  { value: 'resource', label: '資源採取' },
  { value: 'settlement', label: '居住地' },
  { value: 'custom', label: 'カスタム' }
]

const scales = [
  { value: 'small', label: '小規模（数百m）' },
  { value: 'medium', label: '中規模（数km）' },
  { value: 'large', label: '大規模（数十km）' },
  { value: 'vast', label: '広大（数百km）' },
  { value: 'continental', label: '大陸規模' }
]

const climates = [
  { value: 'tropical', label: '熱帯' },
  { value: 'temperate', label: '温帯' },
  { value: 'arctic', label: '極寒' },
  { value: 'arid', label: '乾燥' },
  { value: 'mediterranean', label: '地中海性' },
  { value: 'monsoon', label: 'モンスーン' },
  { value: 'continental', label: '大陸性' },
  { value: 'oceanic', label: '海洋性' },
  { value: 'magical', label: '魔法的気候' }
]

export function LocationForm({ onGenerate, isGenerating, initialData, useStreaming, onStreamingChange }: LocationFormProps) {
  const [mode, setMode] = useState<'standard' | 'idea' | 'batch'>(initialData?.mode || 'standard')
  const [formData, setFormData] = useState<LocationInput>(initialData || {
    mode: 'standard',
    locationType: undefined, // おまかせ
    genre: undefined, // おまかせ
    purpose: undefined, // おまかせ
    scale: undefined, // おまかせ
    climate: undefined, // おまかせ
    dangerLevel: 5,
    accessDifficulty: 5,
    explorationLevel: 7,
    creativityLevel: 7
  })
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { savedWorlds } = useWorldBuilderStore()
  const { savedCommunities } = useCommunityStore()
  const { enemies: savedEnemies } = useEnemyStore()
  const { items: savedItems } = useItemStore()
  const { characters: savedCharacters } = useCharacterStore()
  const { 
    customValues,
    getCustomValues,
    addCustomValue,
    useCustomValue,
    init: initCustomValues
  } = useCustomValuesStore()
  
  // ロケーションジェネレータ用のカスタム値を取得
  const savedLocationTypes = getCustomValues('locationType' as any, 'location')
  const savedGenres = getCustomValues('genre' as any, 'location')
  const savedPurposes = getCustomValues('purpose' as any, 'location')
  
  useEffect(() => {
    initCustomValues()
  }, [])

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    onGenerate({ ...formData, mode })
  }, [formData, mode, onGenerate])

  const updateFormData = useCallback((updates: Partial<LocationInput>) => {
    setFormData(prev => ({ ...prev, ...updates }))
  }, [])
  
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    const reader = new FileReader()
    reader.onload = (event) => {
      const text = event.target?.result as string
      updateFormData({ uploadedContent: text })
    }
    reader.readAsText(file)
  }, [updateFormData])

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <CardHeader>
        <CardTitle>ロケーション設定</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs value={mode} onValueChange={(v) => setMode(v as typeof mode)}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="standard">標準</TabsTrigger>
            <TabsTrigger value="idea">アイディア</TabsTrigger>
            <TabsTrigger value="batch">バッチ</TabsTrigger>
          </TabsList>

          <TabsContent value="standard" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">ロケーション名（オプション）</Label>
                <Input
                  id="name"
                  value={formData.name || ''}
                  onChange={(e) => updateFormData({ name: e.target.value })}
                  placeholder="自動生成されます"
                />
              </div>
              <div>
                <Label htmlFor="locationType">ロケーションタイプ</Label>
                <Select
                  value={formData.locationType || ''}
                  onValueChange={(v) => updateFormData({ locationType: v === 'none' ? undefined : v as any })}
                >
                  <SelectTrigger id="locationType">
                    <SelectValue placeholder="おまかせ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">おまかせ</SelectItem>
                    {locationTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                    {savedLocationTypes.map(custom => (
                      <SelectItem key={custom.id} value={custom.value}>
                        {custom.label} (カスタム)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.locationType === 'custom' && (
              <div>
                <Label htmlFor="customType">カスタムタイプ</Label>
                <div className="flex gap-2">
                  <Input
                    id="customType"
                    value={formData.customType || ''}
                    onChange={(e) => updateFormData({ customType: e.target.value })}
                    placeholder="例：古代遺跡の森"
                    className="flex-1"
                  />
                  {formData.customType && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (formData.customType) {
                          addCustomValue('locationType' as any, formData.customType, formData.customType, 'location')
                        }
                      }}
                    >
                      保存
                    </Button>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="genre">ジャンル</Label>
                <Select
                  value={formData.genre || ''}
                  onValueChange={(v) => updateFormData({ genre: v === 'none' ? undefined : v as any })}
                >
                  <SelectTrigger id="genre">
                    <SelectValue placeholder="おまかせ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">おまかせ</SelectItem>
                    {genres.map(genre => (
                      <SelectItem key={genre.value} value={genre.value}>
                        {genre.label}
                      </SelectItem>
                    ))}
                    {savedGenres.map(custom => (
                      <SelectItem key={custom.id} value={custom.value}>
                        {custom.label} (カスタム)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="purpose">用途・目的</Label>
                <Select
                  value={formData.purpose || ''}
                  onValueChange={(v) => updateFormData({ purpose: v === 'none' ? undefined : v as any })}
                >
                  <SelectTrigger id="purpose">
                    <SelectValue placeholder="おまかせ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">おまかせ</SelectItem>
                    {purposes.map(purpose => (
                      <SelectItem key={purpose.value} value={purpose.value}>
                        {purpose.label}
                      </SelectItem>
                    ))}
                    {savedPurposes.map(custom => (
                      <SelectItem key={custom.id} value={custom.value}>
                        {custom.label} (カスタム)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {formData.genre === 'custom' && (
              <div>
                <Label htmlFor="customGenre">カスタムジャンル</Label>
                <div className="flex gap-2">
                  <Input
                    id="customGenre"
                    value={formData.customGenre || ''}
                    onChange={(e) => updateFormData({ customGenre: e.target.value })}
                    placeholder="例：サイバーファンタジー"
                    className="flex-1"
                  />
                  {formData.customGenre && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (formData.customGenre) {
                          addCustomValue('genre' as any, formData.customGenre, formData.customGenre, 'location')
                        }
                      }}
                    >
                      保存
                    </Button>
                  )}
                </div>
              </div>
            )}
            
            {formData.purpose === 'custom' && (
              <div>
                <Label htmlFor="customPurpose">カスタム用途</Label>
                <div className="flex gap-2">
                  <Input
                    id="customPurpose"
                    value={formData.customPurpose || ''}
                    onChange={(e) => updateFormData({ customPurpose: e.target.value })}
                    placeholder="例：異次元への入り口"
                    className="flex-1"
                  />
                  {formData.customPurpose && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (formData.customPurpose) {
                          addCustomValue('purpose' as any, formData.customPurpose, formData.customPurpose, 'location')
                        }
                      }}
                    >
                      保存
                    </Button>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="scale">規模</Label>
                <Select
                  value={formData.scale || ''}
                  onValueChange={(v) => updateFormData({ scale: v === 'none' ? undefined : v as any })}
                >
                  <SelectTrigger id="scale">
                    <SelectValue placeholder="おまかせ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">おまかせ</SelectItem>
                    {scales.map(scale => (
                      <SelectItem key={scale.value} value={scale.value}>
                        {scale.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="climate">気候</Label>
                <Select
                  value={formData.climate || ''}
                  onValueChange={(v) => updateFormData({ climate: v === 'none' ? undefined : v as any })}
                >
                  <SelectTrigger id="climate">
                    <SelectValue placeholder="おまかせ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">おまかせ</SelectItem>
                    {climates.map(climate => (
                      <SelectItem key={climate.value} value={climate.value}>
                        {climate.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="dangerLevel">
                  危険度: {formData.dangerLevel || 5}/10
                </Label>
                <Slider
                  id="dangerLevel"
                  min={1}
                  max={10}
                  step={1}
                  value={[formData.dangerLevel || 5]}
                  onValueChange={([v]) => updateFormData({ dangerLevel: v })}
                  className="mt-2"
                />
                <p className="text-xs text-gray-600 mt-1">
                  1: 完全に安全 / 10: 極めて危険
                </p>
              </div>

              <div>
                <Label htmlFor="accessDifficulty">
                  アクセス難易度: {formData.accessDifficulty || 5}/10
                </Label>
                <Slider
                  id="accessDifficulty"
                  min={1}
                  max={10}
                  step={1}
                  value={[formData.accessDifficulty || 5]}
                  onValueChange={([v]) => updateFormData({ accessDifficulty: v })}
                  className="mt-2"
                />
                <p className="text-xs text-gray-600 mt-1">
                  1: 容易にアクセス可能 / 10: 到達困難
                </p>
              </div>

              <div>
                <Label htmlFor="explorationLevel">
                  探索価値: {formData.explorationLevel || 7}/10
                </Label>
                <Slider
                  id="explorationLevel"
                  min={1}
                  max={10}
                  step={1}
                  value={[formData.explorationLevel || 7]}
                  onValueChange={([v]) => updateFormData({ explorationLevel: v })}
                  className="mt-2"
                />
                <p className="text-xs text-gray-600 mt-1">
                  1: 既知の場所 / 10: 未開の地
                </p>
              </div>
              
              <div>
                <Label htmlFor="creativityLevel">
                  創造性: {formData.creativityLevel || 7}/10
                </Label>
                <Slider
                  id="creativityLevel"
                  min={1}
                  max={10}
                  step={1}
                  value={[formData.creativityLevel || 7]}
                  onValueChange={([v]) => updateFormData({ creativityLevel: v })}
                  className="mt-2"
                />
                <p className="text-xs text-gray-600 mt-1">
                  1: 現実的 / 10: 幻想的
                </p>
              </div>
            </div>

            {savedWorlds.length > 0 && (
              <div>
                <Label htmlFor="worldContext">世界観連携（オプション）</Label>
                <Select
                  value={formData.worldContext || ''}
                  onValueChange={(v) => updateFormData({ worldContext: v === 'none' ? undefined : v })}
                >
                  <SelectTrigger id="worldContext">
                    <SelectValue placeholder="世界観を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">なし</SelectItem>
                    {savedWorlds.map(world => (
                      <SelectItem key={world.id} value={world.id}>
                        {world.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {savedCommunities.length > 0 && (
              <div>
                <Label htmlFor="communityContext">コミュニティ連携（オプション）</Label>
                <Select
                  value={formData.communityContext || ''}
                  onValueChange={(v) => updateFormData({ communityContext: v === 'none' ? undefined : v })}
                >
                  <SelectTrigger id="communityContext">
                    <SelectValue placeholder="コミュニティを選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">なし</SelectItem>
                    {savedCommunities.map(community => (
                      <SelectItem key={community.id} value={community.id}>
                        {community.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label htmlFor="additionalSettings">追加設定（オプション）</Label>
              <Textarea
                id="additionalSettings"
                value={formData.additionalSettings || ''}
                onChange={(e) => updateFormData({ additionalSettings: e.target.value })}
                placeholder="特殊な要望や詳細設定を入力"
                rows={3}
              />
            </div>
          </TabsContent>

          <TabsContent value="idea" className="space-y-4">
            <div>
              <Label htmlFor="ideaText">アイディア入力</Label>
              <Textarea
                id="ideaText"
                value={formData.ideaText || ''}
                onChange={(e) => updateFormData({ ideaText: e.target.value })}
                placeholder="例：巨大な水晶が浮遊する森で、月光を浴びると道が現れる神秘的な場所"
                rows={6}
                required={mode === 'idea'}
              />
              <p className="text-xs text-gray-600 mt-1">
                自由な発想でロケーションのアイディアを入力してください
              </p>
            </div>
            
            <div>
              <Label>またはファイルをアップロード</Label>
              <div className="mt-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.md"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  ファイルを選択
                </Button>
                {formData.uploadedContent && (
                  <p className="text-sm text-muted-foreground mt-2">
                    ファイルが読み込まれました
                  </p>
                )}
              </div>
            </div>
            
            <div>
              <Label htmlFor="creativityLevel-idea">
                創造性: {formData.creativityLevel || 7}/10
              </Label>
              <Slider
                id="creativityLevel-idea"
                min={1}
                max={10}
                step={1}
                value={[formData.creativityLevel || 7]}
                onValueChange={([v]) => updateFormData({ creativityLevel: v })}
                className="mt-2"
              />
              <p className="text-xs text-gray-600 mt-1">
                1: 現実的 / 10: 幻想的
              </p>
            </div>
            
            {savedWorlds.length > 0 && (
              <div>
                <Label htmlFor="worldContext-idea">世界観連携（オプション）</Label>
                <Select
                  value={formData.worldContext || ''}
                  onValueChange={(v) => updateFormData({ worldContext: v === 'none' ? undefined : v })}
                >
                  <SelectTrigger id="worldContext-idea">
                    <SelectValue placeholder="世界観を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">なし</SelectItem>
                    {savedWorlds.map(world => (
                      <SelectItem key={world.id} value={world.id}>
                        {world.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {savedCommunities.length > 0 && (
              <div>
                <Label htmlFor="communityContext-idea">コミュニティ連携（オプション）</Label>
                <Select
                  value={formData.communityContext || ''}
                  onValueChange={(v) => updateFormData({ communityContext: v === 'none' ? undefined : v })}
                >
                  <SelectTrigger id="communityContext-idea">
                    <SelectValue placeholder="コミュニティを選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">なし</SelectItem>
                    {savedCommunities.map(community => (
                      <SelectItem key={community.id} value={community.id}>
                        {community.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </TabsContent>

          <TabsContent value="batch" className="space-y-4">
            <div>
              <Label htmlFor="numberOfLocations">生成数</Label>
              <Input
                id="numberOfLocations"
                type="number"
                min={2}
                max={10}
                value={formData.numberOfLocations || 3}
                onChange={(e) => updateFormData({ numberOfLocations: parseInt(e.target.value) })}
                required={mode === 'batch'}
              />
              <p className="text-xs text-gray-600 mt-1">
                2〜10個のロケーションを一度に生成できます
              </p>
            </div>

            <div>
              <Label htmlFor="batchTheme">統一テーマ</Label>
              <Input
                id="batchTheme"
                value={formData.batchTheme || ''}
                onChange={(e) => updateFormData({ batchTheme: e.target.value })}
                placeholder="例：古代文明の遺跡群"
              />
            </div>

            <div>
              <Label htmlFor="batchPattern">生成パターン</Label>
              <Select
                value={formData.batchPattern || 'random'}
                onValueChange={(v) => updateFormData({ batchPattern: v as any })}
              >
                <SelectTrigger id="batchPattern">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="connected">関連性のある場所</SelectItem>
                  <SelectItem value="random">独立した多様な場所</SelectItem>
                  <SelectItem value="progression">段階的に変化する場所</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.batchPattern === 'connected' && (
              <div>
                <Label htmlFor="connectionType">関連タイプ</Label>
                <Select
                  value={formData.connectionType || 'trade_route'}
                  onValueChange={(v) => updateFormData({ connectionType: v as any })}
                >
                  <SelectTrigger id="connectionType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trade_route">交易路</SelectItem>
                    <SelectItem value="pilgrimage">巡礼路</SelectItem>
                    <SelectItem value="migration">移動経路</SelectItem>
                    <SelectItem value="exploration">探索ルート</SelectItem>
                    <SelectItem value="conquest">征服経路</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label htmlFor="creativityLevel-batch">
                創造性: {formData.creativityLevel || 7}/10
              </Label>
              <Slider
                id="creativityLevel-batch"
                min={1}
                max={10}
                step={1}
                value={[formData.creativityLevel || 7]}
                onValueChange={([v]) => updateFormData({ creativityLevel: v })}
                className="mt-2"
              />
              <p className="text-xs text-gray-600 mt-1">
                1: 現実的 / 10: 幻想的
              </p>
            </div>
            
            {savedWorlds.length > 0 && (
              <div>
                <Label htmlFor="worldContext-batch">世界観連携（オプション）</Label>
                <Select
                  value={formData.worldContext || ''}
                  onValueChange={(v) => updateFormData({ worldContext: v === 'none' ? undefined : v })}
                >
                  <SelectTrigger id="worldContext-batch">
                    <SelectValue placeholder="世界観を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">なし</SelectItem>
                    {savedWorlds.map(world => (
                      <SelectItem key={world.id} value={world.id}>
                        {world.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {savedCommunities.length > 0 && (
              <div>
                <Label htmlFor="communityContext-batch">コミュニティ連携（オプション）</Label>
                <Select
                  value={formData.communityContext || ''}
                  onValueChange={(v) => updateFormData({ communityContext: v === 'none' ? undefined : v })}
                >
                  <SelectTrigger id="communityContext-batch">
                    <SelectValue placeholder="コミュニティを選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">なし</SelectItem>
                    {savedCommunities.map(community => (
                      <SelectItem key={community.id} value={community.id}>
                        {community.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label htmlFor="additionalSettings-batch">追加設定（オプション）</Label>
              <Textarea
                id="additionalSettings-batch"
                value={formData.additionalSettings || ''}
                onChange={(e) => updateFormData({ additionalSettings: e.target.value })}
                placeholder="特殊な要望や詳細設定を入力"
                rows={3}
              />
            </div>
          </TabsContent>
        </Tabs>

        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="useProMode"
              checked={formData.useProMode || false}
              onCheckedChange={(v) => updateFormData({ useProMode: v === true })}
            />
            <Label htmlFor="useProMode" className="cursor-pointer">
              Proモード（より詳細な生成）
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="useGemini3FlashMode"
              checked={formData.useGemini3FlashMode || false}
              onCheckedChange={(v) => updateFormData({ useGemini3FlashMode: v === true })}
            />
            <Label htmlFor="useGemini3FlashMode" className="cursor-pointer">
              Gemini 3 Flash モード（試験版）
            </Label>
          </div>

          {onStreamingChange && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="useStreaming"
                checked={useStreaming ?? true}
                onCheckedChange={(checked) => onStreamingChange(checked === true)}
              />
              <Label htmlFor="useStreaming" className="cursor-pointer">
                ストリーミング生成（リアルタイム表示）
              </Label>
            </div>
          )}
        </div>

        <Button
          type="submit"
          disabled={isGenerating}
          className="w-full"
        >
          {isGenerating ? (
            <>
              <MapPin className="mr-2 h-4 w-4 animate-spin" />
              生成中...
            </>
          ) : (
            <>
              <MapPin className="mr-2 h-4 w-4" />
              ロケーションを生成
            </>
          )}
        </Button>
      </CardContent>
    </form>
  )
}